import { useCallback } from "react";
import { X } from "lucide-react";
import { _t } from "@/i18n/t";
import type { ToastAction, TypeHeroToast } from "./types";
import { TOAST_ICONS, TOAST_COLORS } from "./theme";
import { useToast } from "./toast-context";

const ACTION_LABEL_KEYS: Record<ToastAction, string> = {
  Retry: "toast.action.retry",
  "Try again": "toast.action.tryAgain",
  Undo: "toast.action.undo",
  "View details": "toast.action.viewDetails",
  Open: "toast.action.open",
  View: "toast.action.view",
  "Contact us": "toast.action.contactUs",
};

export const ToastCard = ({ toast }: { toast: TypeHeroToast }) => {
  const { dismiss, pauseToast, resumeToast } = useToast();

  const Icon = TOAST_ICONS[toast.type];
  const colors = TOAST_COLORS[toast.type];

  const handleMouseEnter = useCallback(() => pauseToast(toast.id), [toast.id, pauseToast]);
  const handleMouseLeave = useCallback(() => resumeToast(toast.id), [toast.id, resumeToast]);

  const isProgress = toast.type === "progress" && toast.progress != null;
  const isError = toast.type === "error-soft" || toast.type === "error-hard";

  const title = toast.title || "";
  const body = toast.body || "";

  return (
    <div
      role={isError ? "alert" : "status"}
      data-testid={`toast-card-kind-${toast.type}`}
      aria-live={isError ? "assertive" : "polite"}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative flex gap-3 rounded-2xl border border-border/60 shadow-lg transition-all px-4 py-3.5 ${toast._exiting ? 'animate-th-exit' : 'animate-th-enter'}`}
      style={{
        background: `linear-gradient(135deg, hsl(var(--card)) 0%, ${colors.accent} 100%)`,
      }}
    >
      <div className="shrink-0 pt-px">
        <Icon
          className={toast.type === "progress" ? "animate-spin" : ""}
          style={{ width: 16, height: 16, color: colors.icon }}
        />
      </div>

      <div className="flex-1 min-w-0 space-y-0.5 pr-5">
        <p className="text-sm font-normal leading-tight text-foreground whitespace-pre-line">
          {title}
        </p>

        {body && (
          <p className="text-[13px] leading-snug text-foreground/80 whitespace-pre-line">
            {body}
          </p>
        )}

        {isProgress && (
          <div className="mt-1.5 h-1 rounded-full bg-border overflow-hidden">
            <div
              data-testid="toast-card-progress"
              role="progressbar"
              aria-label={toast.title}
              aria-valuenow={Math.round(toast.progress ?? 0)}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${toast.progress}%`, backgroundColor: colors.icon }}
            />
          </div>
        )}

        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-1 border-0 bg-transparent p-0 text-[13px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            style={{ color: colors.icon }}
          >
            {_t(ACTION_LABEL_KEYS[toast.action.label])}
          </button>
        )}
      </div>

      <button
        onClick={() => dismiss(toast.id)}
        className="absolute top-3 right-3 border-0 bg-transparent text-foreground/50 hover:text-foreground/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        aria-label="Dismiss notification"
        data-testid="toast-card-dismiss"
      >
        <X style={{ width: 16, height: 16 }} />
      </button>
    </div>
  );
};
